/**
 * Shared label resolution utilities for audience-data UIs.
 *
 * Centralizes fallback behavior so previews, mapping screens, and filters
 * all render the same user-facing label for a column.
 */

type LabelSource = {
  name?: unknown
  sourceColumn?: unknown
  label?: unknown
  displayLabel?: unknown
  isOperational?: boolean
  /** Original SPSS variable name — used as rawName anchor for code prefix stripping. */
  originalName?: unknown
}

/**
 * Descriptor suffixes to remove from variable labels.
 * These are metadata tags that appear in SAV files but aren't part of the question text.
 */
const DESCRIPTOR_SUFFIXES = [
  'Freetext',
  'Free text',
  'Open text',
  'Open ended',
  'Open end',
  'Other specify',
  'Other (specify)',
  'Other - please specify',
  'Other, please specify',
  'Verbatim',
  'Comments',
  'Text response',
  'Open response',
] as const

// Rich-text HTML wrapper tags embedded by some survey authoring tools,
// e.g. "<p>What is your age?</p>".
const HTML_TAG_PATTERN = /<[^>]+>/g
// SurveyJS/Decipher-style templating tokens embedded in labels, e.g. "{#up2}".
const TEMPLATE_TOKEN_PATTERN = /\{#[^}]*\}/g
// NX/Decipher-style pipe placeholders in labels: "[pipe: ...]".
const PIPE_REFERENCE_PATTERN = /\[\s*pipe\s*:[^\]]+\]/gi
// Generic template placeholders: "${...}" and "$ {...}".
const DOLLAR_TEMPLATE_PATTERN = /\$\s*\{[^}]+\}/g
// Placeholder remnants after stripping.
const THE_FOLLOWING_ORPHAN_PATTERN = /\b(the\s+following)\s*:\s*\?\s*/gi
const TRAILING_COLON_ORPHAN_PATTERN = /\s*:\s*\?\s*$/i
// Scale-instruction parentheticals.
const SCALE_INSTRUCTION_PAREN_PATTERN = /\(\s*(?:with\s+)?(?:10|[0-9])\s+being\b[^)]*\)/gi

function hasPipePlaceholder(value: string): boolean {
  return /\[\s*pipe\s*:[^\]]+\]/i.test(value)
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

function coerceLabelValue(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim()
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    const selected = record.selected
    if (typeof selected === 'string' || typeof selected === 'number' || typeof selected === 'boolean') {
      return String(selected).trim()
    }
  }
  return ''
}

function isLikelyBrokenLabel(candidate: string): boolean {
  return candidate.length < 3
}

type CleanLabelOptions = {
  /**
   * Preserve already-resolved pipe text in labels.
   * If unresolved placeholders are still present, callers can retry with stripping on.
   *
   * Default behavior is `true` (pipe placeholders are stripped).
   * Display paths that should preserve already-resolved piped text must pass `false`
   * first, then optionally retry with `true` as a fallback.
   */
  stripPipeReferences?: boolean
}

/**
 * Removes technical prefix noise from parser labels when possible.
 *
 * Cleans SAV variable labels by removing:
 * 1. Known descriptor suffixes (": Freetext", ": Open text", etc.)
 * 2. Variable code prefixes when they duplicate the raw variable name
 *
 * Preserves:
 * - MR answer option suffixes (": Blue Berries")
 * - Legitimate colons in questions ("How satisfied with: Service")
 * - Already clean labels
 *
 * Example:
 * - rawName: "Q2_98t"
 * - label:   "Q2_98t Bij welke van... : Freetext"
 * - output:  "Bij welke van..."
 */
export function cleanLabel(label: string, rawName?: string, options: CleanLabelOptions = {}): string {
  const normalized = normalizeWhitespace(label)
  if (!normalized || normalized.length < 3) return normalized

  let cleaned = normalized
  const stripPipeReferences = options.stripPipeReferences ?? true

  // Step -1: Strip HTML markup and SurveyJS/Decipher template tokens embedded
  // by rich-text survey authoring tools, e.g.
  // "<p>What is your age?</p>" -> "What is your age?"
  // "<p>I feel closer to…</p>{#up2}" -> "I feel closer to…"
  if (cleaned.includes('<') || cleaned.includes('{#')) {
    const stripped = normalizeWhitespace(
      cleaned.replace(HTML_TAG_PATTERN, ' ').replace(TEMPLATE_TOKEN_PATTERN, ''),
    )
    if (stripped) cleaned = stripped
  }

  // Step 0: Strip trailing disambiguator parenthesized variable codes.
  // _rename_columns_with_labels appends "(originalName)" when duplicate
  // display names exist. NX exports use spaces instead of underscores
  // (e.g. "Per User Pricing (Q SpendLastReason 0r2)").
  // Two triggers:
  //   a) rawName matches the parens content (with flexible normalization)
  //   b) Content looks code-like (letter+digit mix, <50 chars)
  const disambigPattern = /\s+\(([^)]+)\)\s*$/
  const disambigMatch = cleaned.match(disambigPattern)
  if (disambigMatch && disambigMatch.index! > 3) {
    const code = disambigMatch[1].trim()
    const before = cleaned.slice(0, disambigMatch.index).trim()
    if (before.length > 3 && code.length < 50) {
      const codeNorm = code.toLowerCase().replace(/[\s_-]+/g, '')
      const rawNorm = rawName ? rawName.toLowerCase().replace(/[\s_-]+/g, '') : ''
      const isRawNameMatch = rawNorm && codeNorm === rawNorm
      const looksCodeLike = /^[a-z]/i.test(code) && /\d/.test(code)
      if (isRawNameMatch || looksCodeLike) {
        cleaned = before
      }
    }
  }

  // Step 1: Remove known descriptor suffixes (after ': ')
  // Only if the suffix matches our known list of metadata descriptors
  for (const suffix of DESCRIPTOR_SUFFIXES) {
    // Match ": Freetext", " : Open text", etc. (with optional spacing)
    const pattern = new RegExp(`\\s*:\\s*${escapeRegex(suffix)}\\s*$`, 'i')
    const match = cleaned.match(pattern)
    if (match) {
      cleaned = cleaned.slice(0, match.index).trim()
      break // Only remove one suffix
    }
  }

  // Step 2: Remove variable code prefix if it duplicates rawName.
  // Handles both simple ("Q2_98t Question") and dot-separated ("A13.SPAIN. Question")
  // patterns by using the rawName as an anchor to locate the code prefix.
  let prefixStripped = false
  if (rawName) {
    const name = normalizeWhitespace(rawName)
    if (name) {
      const escaped = escapeRegex(name)

      // 2a: Exact match — label starts with "rawName" + optional delimiter (colon/dot) + space.
      // Handles: "vQ7: Question text", "QB1. Question text", "Q2_98t Question text"
      const exactPattern = new RegExp(`^${escaped}(?:\\s*[:/|\\-]\\s*|\\.\\s+|\\s+)`, 'i')
      const exactMatch = cleaned.match(exactPattern)
      if (exactMatch) {
        const afterPrefix = cleaned.slice(exactMatch[0].length).trim()
        if (afterPrefix.length >= 9) {
          cleaned = afterPrefix
          prefixStripped = true
        }
      }

      // 2b: Dot/underscore-separated prefix containing the rawName.
      // Example: rawName="A13", label="A13.SPAIN. What is the highest..."
      // Handles both single-token ("A13" in ["A13","SPAIN"]) and multi-token
      // ("B2_EMEA" matching ["B2","EMEA"] as prefix of ["B2","EMEA","3"]).
      if (!prefixStripped) {
        // Colon added alongside dot/underscore to handle "vQ12r1: Child 1 - ..." conventions
        const dotPrefixPattern = /^([A-Za-z0-9]+(?:[._][A-Za-z0-9]+)*)[._:]?\s+(.{9,})/
        const dotMatch = cleaned.match(dotPrefixPattern)
        if (dotMatch) {
          const prefixBlock = dotMatch[1]
          const rest = dotMatch[2]
          const prefixTokens = prefixBlock.toLowerCase().split(/[._]/)
          const varTokens = name.toLowerCase().split(/[._]/)
          const anchored =
            prefixTokens.includes(name.toLowerCase()) ||
            varTokens.every((t, i) => prefixTokens[i] === t)
          if (anchored && rest.trim().length >= 9) {
            cleaned = rest.trim()
            prefixStripped = true
          }
        }
      }

      // 2c: Remove direct repeats with delimiters (Q2_98t / Bij welke...)
      if (!prefixStripped) {
        cleaned = cleaned
          .replace(new RegExp(`^${escaped}\\s*[\\/:|\\-]\\s*`, 'i'), '')
          .replace(new RegExp(`^${escaped}\\s*[\\/:|\\-]\\s*${escaped}\\s*`, 'i'), '')
      }
    }
  }

  // Step 3: Remove common survey code prefix patterns (VAR001, S1a, B1, vQ7, etc.)
  // 3a: Code + optional delimiter pattern ("B1 Here is...", "vQ7: Question text", "QB1. Question")
  // Case-insensitive to handle both "QB9r1" and "vQ7" conventions.
  if (!prefixStripped) {
    const codePattern = /^([A-Za-z]+\d+[A-Za-z\d_]*)\s*[.:]?\s+(.{9,})/i
    const codeMatch = cleaned.match(codePattern)
    if (codeMatch) {
      const prefix = codeMatch[1]
      if (!rawName || prefix.toLowerCase() !== rawName.toLowerCase()) {
        cleaned = codeMatch[2]
        prefixStripped = true
      }
    }
  }

  // 3b: Code + dot/colon/separator pattern ("B1. Here is...", "vQ7: Question", "A13.SPAIN. What is...")
  // Runs even without rawName — detects code-like prefix by pattern alone.
  // Handles legacy stored labels where the SPSS var name was lost.
  // Pattern requires at least one letter + one digit to avoid false positives
  // on words like "How." or "What." — true survey codes always mix letters and digits.
  // Case-insensitive to handle lowercase v prefix (vQ7, vHIDSEGDH).
  if (!prefixStripped) {
    const dotCodePattern = /^([A-Za-z]+\d[\w]*(?:[._][A-Za-z0-9]+)*)[._:]\s+(.{9,})/i
    const dotCodeMatch = cleaned.match(dotCodePattern)
    if (dotCodeMatch) {
      const prefix = dotCodeMatch[1]
      const rest = dotCodeMatch[2]
      if (prefix.length <= 20 && rest.trim().length >= 9) {
        cleaned = rest.trim()
        prefixStripped = true
      }
    }
  }

  // Step 4: Strip pipe/template placeholders and orphan "?" artifacts.
  cleaned = cleaned
    .replace(DOLLAR_TEMPLATE_PATTERN, ' ')
    .replace(THE_FOLLOWING_ORPHAN_PATTERN, '$1')
    .replace(TRAILING_COLON_ORPHAN_PATTERN, '')
  if (stripPipeReferences) {
    cleaned = cleaned.replace(PIPE_REFERENCE_PATTERN, ' ')
  }

  // Step 5: Strip scale-instruction parentheticals after code stripping.
  cleaned = cleaned.replace(SCALE_INSTRUCTION_PAREN_PATTERN, ' ')
  cleaned = cleaned.replace(/\s+([?!.,;:])/g, '$1')

  cleaned = normalizeWhitespace(cleaned)
  return isLikelyBrokenLabel(cleaned) ? normalized : cleaned
}

/**
 * Humanize raw column keys for display fallback.
 * Keeps this conservative to avoid misleading labels.
 */
export function humanizeColumnName(raw: string): string {
  const base = normalizeWhitespace(raw.replace(/^open_text_/i, ''))
  if (!base) return raw

  return base
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function getRawName(input: LabelSource): string {
  return coerceLabelValue(input.sourceColumn) || coerceLabelValue(input.name)
}

/**
 * Label fallback chain:
 * displayLabel (cleaned with originalName) -> cleaned label -> humanized raw name -> raw name.
 *
 * When `originalName` is present (SPSS data), it's used as the rawName anchor
 * for `cleanLabel()` to strip code prefixes like "B1." or "A13.SPAIN." from
 * the display label — even if the label was stored before our Python-side
 * cleaning was added.
 */
export function resolveDisplayLabel(input: LabelSource): string {
  const rawName = getRawName(input)
  // Prefer originalName (SPSS var code) as the cleaning anchor
  const cleaningAnchor = coerceLabelValue(input.originalName) || rawName

  const display = coerceLabelValue(input.displayLabel)
  if (display) {
    const cleaned = cleanLabel(display, cleaningAnchor, { stripPipeReferences: false })
    return hasPipePlaceholder(cleaned)
      ? cleanLabel(cleaned, cleaningAnchor, { stripPipeReferences: true })
      : cleaned
  }

  const parserLabel = coerceLabelValue(input.label)
  if (parserLabel) {
    const cleaned = cleanLabel(parserLabel, cleaningAnchor, { stripPipeReferences: false })
    return hasPipePlaceholder(cleaned)
      ? cleanLabel(cleaned, cleaningAnchor, { stripPipeReferences: true })
      : cleaned
  }

  if (rawName) {
    // Try cleanLabel first — strips code prefixes (B1., VAR001) while preserving
    // stem separators (" - ", ": ") needed for multi-select grouping.
    // Only fall back to humanizeColumnName for raw codes that cleanLabel can't improve.
    const cleaned = cleanLabel(rawName)
    if (cleaned !== normalizeWhitespace(rawName)) return cleaned
    return humanizeColumnName(rawName)
  }

  return ''
}

/* -------------------------------------------------------------------------- */
/*  2D grid variable detection                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Matches SPSS 2D grid variable names flattened to columns.
 * Row = brand (tracked entity), Column = response scale (awareness level, etc.)
 *
 * Examples:
 *   D_UseAwareness_0r1c2       → prefix "D_UseAwareness",  row 1, col 2
 *   Q_ProductSpendReduction_r3c1 → prefix "Q_ProductSpendReduction", row 3, col 1
 */
const GRID_VAR_PATTERN = /^(.+?)_0?r(\d+)c(\d+)$/

/**
 * Matches double-rN 2D grid names (Confirmit/Forsta brand funnels).
 * Lr{N} = brand row, trailing r{M} = funnel-stage column.
 *
 * Examples:
 *   QC3_Lr4r8   → prefix "QC3", row 4, col 8
 *   QC3_Lr1r17  → prefix "QC3", row 1, col 17
 */
const DOUBLE_RN_GRID_PATTERN = /^(.+?)_Lr(\d+)r(\d+)$/

/** A single cell in a 2D grid family (one variable in the flattened dataset). */
export interface GridCell {
  /** Source column key (used for mapping lookups). */
  key: string
  /** Display label resolved for this cell. */
  fullLabel: string
  /** Row index from the SPSS grid (brand axis). */
  rowIndex: number
  /** Column index from the SPSS grid (response-scale axis). */
  colIndex: number
}

/**
 * A 2D grid family — a set of variables sharing the same SPSS prefix
 * that span ≥ 2 rows AND ≥ 2 columns. Produced by `detectGridFamilies`.
 */
export interface GridFamily {
  /** Variable name prefix shared by all cells (e.g. "D_UseAwareness"). */
  prefix: string
  /** Human-readable stem for the grid header (humanized prefix). */
  stem: string
  /** All cells in the grid, each with row/col coordinates. */
  cells: GridCell[]
  /** Sorted distinct row indices (brand axis). */
  rowIndices: number[]
  /** Sorted distinct column indices (response-scale axis). */
  colIndices: number[]
  /**
   * Column labels from display labels, one per column index.
   * The first cell seen for each column index provides the label.
   */
  colLabels: Map<number, string>
  /**
   * Pre-resolved brand names from Phase 0, keyed by row index.
   * When present, ``convertGridFamily`` skips the sibling-based brand
   * resolution and uses these names directly.
   */
  brandNames?: Map<number, string>
}

/**
 * Detect 2D grid variable families among ungrouped singles.
 *
 * Scans `originalName` for the `PREFIX_0?r{row}c{col}` pattern,
 * groups by PREFIX, and keeps only families with ≥ 2 distinct rows
 * AND ≥ 2 distinct columns (degenerate 1-row/1-col grids stay as singles).
 *
 * Designed to run between Phase 2 (stem promotion) and Phase 3
 * (prefix grouping) so that Phase 3 never re-groups grid variables
 * into incorrect per-brand clusters.
 *
 * @param singles         - Ungrouped singles from Phase 2.
 * @param originalNames   - Map of sourceColumn → SPSS variable name.
 * @returns Grid families extracted, plus the remaining singles (grid members removed).
 */
export function detectGridFamilies(
  singles: { fullLabel: string; key: string }[],
  originalNames: Record<string, string>,
): { gridFamilies: GridFamily[]; remainingSingles: { fullLabel: string; key: string }[] } {
  const prefixBuckets = new Map<string, GridCell[]>()

  for (const single of singles) {
    const origName = originalNames[single.key]
    if (!origName) continue
    const match = origName.match(GRID_VAR_PATTERN) ?? origName.match(DOUBLE_RN_GRID_PATTERN)
    if (!match) continue
    const [, prefix, rowStr, colStr] = match
    prefixBuckets.set(prefix, prefixBuckets.get(prefix) ?? [])
    prefixBuckets.get(prefix)!.push({
      key: single.key,
      fullLabel: single.fullLabel,
      rowIndex: parseInt(rowStr, 10),
      colIndex: parseInt(colStr, 10),
    })
  }

  const gridFamilies: GridFamily[] = []
  const gridKeys = new Set<string>()

  for (const [prefix, cells] of prefixBuckets) {
    const distinctRows = new Set(cells.map((c) => c.rowIndex))
    const distinctCols = new Set(cells.map((c) => c.colIndex))
    if (distinctRows.size < 2 || distinctCols.size < 2) continue

    const colLabels = new Map<number, string>()
    for (const cell of cells) {
      if (!colLabels.has(cell.colIndex)) {
        colLabels.set(cell.colIndex, cell.fullLabel)
      }
    }

    const trimmedPrefix = prefix.replace(/^[A-Z]_/i, '').replace(/_0$/, '')
    const stem = humanizeColumnName(trimmedPrefix || prefix)

    gridFamilies.push({
      prefix,
      stem,
      cells,
      rowIndices: [...distinctRows].sort((a, b) => a - b),
      colIndices: [...distinctCols].sort((a, b) => a - b),
      colLabels,
    })
    for (const cell of cells) gridKeys.add(cell.key)
  }

  const remainingSingles = singles.filter((s) => !gridKeys.has(s.key))
  return { gridFamilies, remainingSingles }
}

/* -------------------------------------------------------------------------- */
/*  Multi-select stem grouping                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Known separators between a shared question stem and per-item answer text
 * in multi-select / battery question labels.
 *
 * Examples:
 *   "Please indicate... - I love to escape"       (" - ")
 *   "Which brands have you purchased: Nike"       (": ")
 *   "Satisfaction with service – Quality"          (" – ")
 *   "Attitudes to life. – I am gender-fluid"      (". – ")
 *
 * Matched greedily (first match from left) via `split(..., 1)`.
 */
export const STEM_SEPARATOR = /\s-\s|:\s| – |\.[ ]?– /

/** A single column belonging to a stem group. */
export interface StemGroupItem {
  /** The unique item portion of the label (after the separator). */
  itemLabel: string
  /** The full original label. */
  fullLabel: string
  /** An opaque key identifying the column (sourceColumn, name, etc.). */
  key: string
}

/** A group of columns sharing the same question stem. */
export interface StemGroup {
  /** Shared question stem text. */
  stem: string
  /** Individual items within the group (always >= 2). */
  items: StemGroupItem[]
}

/** A single entry in the ordered interleaved sequence. */
export type OrderedStemEntry =
  | { type: 'group'; group: StemGroup }
  | { type: 'single'; fullLabel: string; key: string }
  | { type: 'grid'; family: GridFamily }

/** Result of grouping: groups of 2+ items, plus ungrouped singles. */
export interface StemGroupResult {
  groups: StemGroup[]
  singles: { fullLabel: string; key: string }[]
  /**
   * Groups and singles interleaved in original column order.
   * Each group appears at the position of its first member column.
   * Use this for rendering that preserves questionnaire order.
   */
  ordered: OrderedStemEntry[]
  /**
   * 2D grid families extracted before Phase 3 prefix grouping.
   * Each family represents a fully-detected SPSS grid question
   * (≥ 2 rows × ≥ 2 columns). Empty when no originalNames are provided
   * or no grid patterns are found.
   */
  gridFamilies: GridFamily[]
}

/**
 * Group column labels by shared question stem.
 *
 * Splits each label at known separators (` - `, `: `, ` – `, `. – `).
 * Columns sharing the same stem portion form a group (minimum 2 items).
 * Columns with no separator or unique stems pass through as singles.
 *
 * When `originalNames` is provided (SPSS data), labels are cleaned with
 * `cleanLabel(label, originalName)` before stem comparison. This strips
 * per-item code prefixes (e.g. `B1_1.`, `B1_2.`) so that multi-select
 * items with different variable codes but the same question stem are
 * correctly grouped.
 *
 * Pure function — no side effects, no API calls, O(n) on column count.
 *
 * @param columns - Array of objects with at least `label` and `key` fields.
 * @param originalNames - Optional map of key → SPSS variable name for prefix stripping.
 * @returns Groups and singles, preserving original order.
 */
export function groupColumnsBySharedStem(
  columns: { label: string; key: string }[],
  originalNames?: Record<string, string>,
): StemGroupResult {
  // Phase 1: split each label and bucket by stem
  type OrderEntry = { type: 'stem'; stem: string } | { type: 'single'; idx: number }
  const stemBuckets = new Map<string, StemGroupItem[]>()
  const order: OrderEntry[] = []
  const singlesList: { fullLabel: string; key: string }[] = []
  const seenStems = new Set<string>()

  for (const col of columns) {
    // Always clean the label to strip code prefixes before splitting.
    // When originalNames is available, use it as anchor for more precise stripping.
    // Without it, cleanLabel still detects code-like prefixes (B1., VAR001) by pattern.
    const cleanedLabel = originalNames?.[col.key]
      ? cleanLabel(col.label, originalNames[col.key])
      : col.label
    const parts = cleanedLabel.split(STEM_SEPARATOR)
    // Only split if stem is substantial (>15 chars) and item is meaningful (>3 chars)
    if (parts.length >= 2 && parts[0].trim().length > 15 && parts.slice(1).join(' ').trim().length > 3) {
      const stem = parts[0].trim()
      const item = parts.slice(1).join(' ').trim()
      if (!stemBuckets.has(stem)) {
        stemBuckets.set(stem, [])
      }
      stemBuckets.get(stem)!.push({ itemLabel: item, fullLabel: col.label, key: col.key })
      if (!seenStems.has(stem)) {
        seenStems.add(stem)
        order.push({ type: 'stem', stem })
      }
    } else {
      const idx = singlesList.length
      singlesList.push({ fullLabel: col.label, key: col.key })
      order.push({ type: 'single', idx })
    }
  }

  // Phase 2: build initial ordered sequence.
  // Walk the order array to produce interleaved entries preserving
  // original questionnaire position. Single-item "groups" are promoted
  // back to singles so only real multi-item groups remain.
  const groups: StemGroup[] = []
  const orderedPre: OrderedStemEntry[] = []
  const singleKeys = new Set<string>()

  for (const entry of order) {
    if (entry.type === 'stem') {
      const items = stemBuckets.get(entry.stem)!
      if (items.length >= 2) {
        const group = { stem: entry.stem, items }
        groups.push(group)
        orderedPre.push({ type: 'group', group })
      } else {
        const single = { fullLabel: items[0].fullLabel, key: items[0].key }
        singlesList.push(single)
        singleKeys.add(single.key)
        orderedPre.push({ type: 'single', ...single })
      }
    } else {
      const single = singlesList[entry.idx]
      singleKeys.add(single.key)
      orderedPre.push({ type: 'single', fullLabel: single.fullLabel, key: single.key })
    }
  }

  // Grid pre-pass: extract 2D grid families (r{R}c{C} pattern) from singles
  // BEFORE Phase 3 so prefix grouping doesn't split them into per-brand clusters.
  let gridFamilies: GridFamily[] = []
  let postGridOrdered = orderedPre
  let postGridSingles = singlesList

  if (originalNames) {
    const singlesFromPre = orderedPre
      .filter((e): e is OrderedStemEntry & { type: 'single' } => e.type === 'single')
      .map((e) => ({ fullLabel: e.fullLabel, key: e.key }))

    const gridResult = detectGridFamilies(singlesFromPre, originalNames)
    gridFamilies = gridResult.gridFamilies

    if (gridFamilies.length > 0) {
      const keyToFamily = new Map<string, GridFamily>()
      for (const family of gridFamilies) {
        for (const cell of family.cells) keyToFamily.set(cell.key, family)
      }
      const gridKeys = new Set(keyToFamily.keys())
      const emittedFamilies = new Set<string>()
      const newOrdered: OrderedStemEntry[] = []

      for (const entry of orderedPre) {
        if (entry.type !== 'single') {
          newOrdered.push(entry)
          continue
        }
        const family = keyToFamily.get(entry.key)
        if (family) {
          if (!emittedFamilies.has(family.prefix)) {
            emittedFamilies.add(family.prefix)
            newOrdered.push({ type: 'grid', family })
          }
        } else {
          newOrdered.push(entry)
        }
      }
      postGridOrdered = newOrdered
      postGridSingles = singlesList.filter((s) => !gridKeys.has(s.key))
    }
  }

  // Phase 3: variable-name prefix grouping for ungrouped singles.
  // When originalNames is provided, detect sibling variables sharing
  // the same parent prefix. Strips row/col grid coordinates, numeric
  // suffixes, and dot-suffixes to find the base variable name.
  // Examples: D_UseAwareness_0r1c1 → D_UseAwareness,
  //           Q_UseAwareness_0r11  → Q_UseAwareness_0,
  //           B1_2 → B1,  Q5.1 → Q5
  // Groups of 3+ siblings become collapsed groups.
  if (originalNames) {
    const CHILD_SUFFIX = /(?:_Lr\d+r\d+|_?0?r\d+c\d+|r\d+|c\d+|_\d+|\.\d+)$/
    const prefixBuckets = new Map<string, { key: string; fullLabel: string; cleanedLabel: string }[]>()

    for (const entry of postGridOrdered) {
      if (entry.type !== 'single') continue
      const origName = originalNames[entry.key]
      if (!origName) continue
      const prefix = origName.replace(CHILD_SUFFIX, '')
      if (prefix === origName) continue
      if (!prefixBuckets.has(prefix)) prefixBuckets.set(prefix, [])
      prefixBuckets.get(prefix)!.push({
        key: entry.key,
        fullLabel: entry.fullLabel,
        cleanedLabel: cleanLabel(entry.fullLabel, origName),
      })
    }

    const prefixGroupedKeys = new Set<string>()
    const prefixGroups: StemGroup[] = []

    for (const [prefix, members] of prefixBuckets) {
      if (members.length < 3) continue
      const trimmedPrefix = prefix
        .replace(/^[A-Z]_/i, '')
        .replace(/_0$/, '')
      const stem = humanizeColumnName(trimmedPrefix || prefix)
      const items: StemGroupItem[] = members.map((m) => ({
        itemLabel: m.cleanedLabel,
        fullLabel: m.fullLabel,
        key: m.key,
      }))
      const group: StemGroup = { stem, items }
      prefixGroups.push(group)
      groups.push(group)
      for (const m of members) prefixGroupedKeys.add(m.key)
    }

    if (prefixGroupedKeys.size > 0) {
      const finalSingles: typeof singlesList = []
      const ordered: OrderedStemEntry[] = []
      const emittedPrefixGroups = new Set<string>()

      for (const entry of postGridOrdered) {
        if (entry.type === 'group' || entry.type === 'grid') {
          ordered.push(entry)
          continue
        }
        if (prefixGroupedKeys.has(entry.key)) {
          const origName = originalNames[entry.key]!
          const prefix = origName.replace(CHILD_SUFFIX, '')
          if (!emittedPrefixGroups.has(prefix)) {
            emittedPrefixGroups.add(prefix)
            const group = prefixGroups.find((g) => g.items.some((i) => i.key === entry.key))
            if (group) ordered.push({ type: 'group', group })
          }
        } else {
          finalSingles.push({ fullLabel: entry.fullLabel, key: entry.key })
          ordered.push(entry)
        }
      }
      return { groups, singles: finalSingles, ordered, gridFamilies }
    }
  }

  return { groups, singles: postGridSingles, ordered: postGridOrdered, gridFamilies }
}

/** Minimum length for a derived group stem to be considered meaningful. */
const GROUP_STEM_MIN_LENGTH = 15

/** Character-level longest common prefix across the given strings. */
function longestCommonPrefixOf(values: Array<string>): string {
  const trimmed = values.map((v) => v.trim()).filter((v) => v !== '')
  if (trimmed.length === 0) return ''
  const shortest = trimmed.reduce((a, b) => (a.length <= b.length ? a : b))
  for (let i = 0; i < shortest.length; i++) {
    const ch = shortest[i]
    if (trimmed.some((v) => v[i] !== ch)) return shortest.slice(0, i)
  }
  return shortest
}

/** Trim a prefix back to the nearest sentence/word boundary so it reads cleanly. */
function trimStemToBoundary(prefix: string): string {
  const trimmed = prefix.trim()
  if (trimmed.length < GROUP_STEM_MIN_LENGTH) return ''
  const last = trimmed[trimmed.length - 1]
  if (last && !' ?.!,;:'.includes(last)) {
    const best = Math.max(
      trimmed.lastIndexOf(' '),
      trimmed.lastIndexOf('?'),
      trimmed.lastIndexOf('.'),
      trimmed.lastIndexOf('!'),
    )
    if (best >= Math.floor(trimmed.length * 0.5)) {
      return trimmed.slice(0, best + 1).trim()
    }
  }
  return trimmed
}

/**
 * Is a label a bare survey variable code (e.g. "Q7r1", "S1", "QB9r1c2")?
 *
 * Such codes must never be shown as a group title — when a group's `parentLabel`
 * is one of these, callers should derive the question stem from member labels
 * instead. Requires a letter+digit mix, no whitespace, and a short length so
 * legitimate one-word labels (e.g. "Demographics", "OneWord") are never flagged.
 */
function isVariableCodeLabel(label: string): boolean {
  const t = label.trim()
  if (!t || /\s/.test(t)) return false
  return t.length <= 20 && /\d/.test(t) && /^[A-Za-z0-9_.-]+$/.test(t)
}

/**
 * Derive a human-readable question stem from a group's member column labels.
 *
 * Mirrors the Python `_derive_group_label_from_columns` and the workflow-console
 * `resolveGroupStem`: prefer the shared text before the separator (": ", " - "),
 * then a character-level common prefix. Labels are run through {@link cleanLabel}
 * first so per-item code prefixes are stripped. Returns "" when nothing
 * meaningful is shared, so callers can fall back to humanizing the group id.
 */
export function deriveGroupStemFromMemberLabels(memberLabels: Array<string>): string {
  const cleaned = memberLabels
    .map((l) => cleanLabel(l))
    .filter((l) => l.trim().length > 0)
  if (cleaned.length < 2) return ''

  const separatorStems: Array<string> = []
  for (const label of cleaned) {
    const parts = label.split(STEM_SEPARATOR)
    if (parts.length >= 2 && parts[0].trim().length > GROUP_STEM_MIN_LENGTH) {
      separatorStems.push(parts[0].trim())
    }
  }
  if (separatorStems.length >= 2) {
    // Identical stems are the common case (e.g. "<question>: Salesforce",
    // "<question>: HubSpot") — return the full stem without boundary trimming,
    // which would otherwise drop the last word of a stem lacking end punctuation.
    if (separatorStems.every((s) => s === separatorStems[0])) {
      return separatorStems[0]
    }
    const common = trimStemToBoundary(longestCommonPrefixOf(separatorStems))
    if (common.length >= GROUP_STEM_MIN_LENGTH) return common
  }

  const lcp = trimStemToBoundary(longestCommonPrefixOf(cleaned))
  if (lcp.length >= GROUP_STEM_MIN_LENGTH) return lcp
  return ''
}

// ---------------------------------------------------------------------------
// Phase 0 — backend-authoritative grouping
// ---------------------------------------------------------------------------

/**
 * Regex to detect per-brand sub-group IDs produced by Phase 0.
 * Matches `pfx_{parentStem}r{brandIndex}` and captures the parent stem
 * and brand index.  Groups sharing the same parent stem form one 2D grid
 * (brands × dimensions).
 */
const BATTERY_GROUP_ID_RE = /^pfx_(.+?)r(\d+)$/

/**
 * Build the same {@link StemGroupResult} from Phase 0 backend groups instead
 * of the frontend heuristic.  When Phase 0 groups are available they are the
 * authoritative source for column structure; the heuristic becomes the
 * fallback for datasets without Phase 0 data (CSV import, edit mode, etc.).
 *
 * **Battery detection:** Phase 0 grid groups whose IDs match
 * `pfx_{stem}r{N}` are merged into one {@link GridFamily} per parent stem.
 * Each sub-group contributes one row (brand), and each column within a
 * sub-group is a dimension (colIndex).  This covers both:
 *   - Decipher rNcN grids (heuristic-detected, already have per-rN groups)
 *   - LLM-detected multi-dimensional batteries (per-brand groups)
 *
 * **Non-grid groups:** Groups with other types (binary, ranking, etc.)
 * become regular {@link StemGroup} entries.
 *
 * **Singles:** Columns not present in any Phase 0 group become singles.
 *
 * Questionnaire order is preserved by emitting each grid/group/single at
 * the position of its first member column.
 */
export function buildPhase0Grouping(
  columns: { label: string; key: string }[],
  phase0Groups: {
    groupId: string
    columns: string[]
    groupType: string
    parentLabel?: string | null
  }[],
  originalNames?: Record<string, string>,
): StemGroupResult {
  const columnIndex = new Map<string, { label: string; key: string }>()
  const columnOrder = new Map<string, number>()
  for (let i = 0; i < columns.length; i++) {
    columnIndex.set(columns[i].key, columns[i])
    columnOrder.set(columns[i].key, i)
  }

  // Step 1: Identify battery parent stems from grid-type groups.
  const parentBuckets = new Map<string, { rn: number; group: typeof phase0Groups[0] }[]>()
  const standaloneGroups: typeof phase0Groups = []

  for (const g of phase0Groups) {
    const gridTypes = new Set(['grid', 'categorical_grid'])
    const m = BATTERY_GROUP_ID_RE.exec(g.groupId)
    if (m && gridTypes.has(g.groupType)) {
      const parentStem = m[1]
      const rn = parseInt(m[2], 10)
      if (!parentBuckets.has(parentStem)) parentBuckets.set(parentStem, [])
      parentBuckets.get(parentStem)!.push({ rn, group: g })
    } else {
      standaloneGroups.push(g)
    }
  }

  // Promote single-member batteries back to standalone groups.
  for (const [stem, entries] of parentBuckets) {
    if (entries.length < 2) {
      for (const e of entries) standaloneGroups.push(e.group)
      parentBuckets.delete(stem)
    }
  }

  // Step 2: Build GridFamily for each battery.
  const gridFamilies: GridFamily[] = []
  const gridKeys = new Set<string>()

  for (const [parentStem, entries] of parentBuckets) {
    entries.sort((a, b) => a.rn - b.rn)

    // Determine dimension labels from the first brand's columns.
    // Each column in a per-brand group is one dimension.  The label after
    // the stem separator (": ") gives the brand name; the distinguishing
    // part across dimensions comes from the difference in column labels
    // or from the originalName (Q_KPIRating10{Dim}_0r{N} → Dim).
    const firstBrand = entries[0]
    const dimCount = firstBrand.group.columns.length
    const dimLabels = new Map<number, string>()

    for (let ci = 0; ci < dimCount; ci++) {
      const colKey = firstBrand.group.columns[ci]
      const origName = originalNames?.[colKey]
      if (origName) {
        const stemMatch = origName.match(/^(.+?)_0?r\d+$/)
        if (stemMatch) {
          const varStem = stemMatch[1]
          const commonPrefix = parentStem.replace(/_0$/, '').replace(/^[A-Z]_/i, '')
          let dimName = varStem
          if (varStem.startsWith(commonPrefix.replace(/_0$/, ''))) {
            dimName = varStem.slice(commonPrefix.replace(/_0$/, '').length)
          }
          dimLabels.set(ci, humanizeColumnName(dimName || varStem))
          continue
        }
      }
      const col = columnIndex.get(colKey)
      if (col) {
        const parts = col.label.split(STEM_SEPARATOR)
        dimLabels.set(ci, parts.length >= 2 ? parts.slice(1).join(' ').trim() : col.label)
      } else {
        dimLabels.set(ci, `Dimension ${ci + 1}`)
      }
    }

    // Build cells and resolve brand names from display labels.
    const cells: GridCell[] = []
    const rowIndices: number[] = []
    const brandNames = new Map<number, string>()

    for (const { rn, group: brandGroup } of entries) {
      rowIndices.push(rn)

      // Resolve brand name from the first column's label (any dimension works,
      // brand name is the same across dimensions for the same rN).
      const firstColKey = brandGroup.columns[0]
      const firstCol = columnIndex.get(firstColKey)
      if (firstCol) {
        const parts = firstCol.label.split(STEM_SEPARATOR)
        brandNames.set(rn, parts.length >= 2
          ? parts.slice(1).join(' ').trim()
          : `Item ${rn}`)
      }

      for (let ci = 0; ci < brandGroup.columns.length; ci++) {
        const colKey = brandGroup.columns[ci]
        const col = columnIndex.get(colKey)
        cells.push({
          key: colKey,
          fullLabel: col?.label ?? colKey,
          rowIndex: rn,
          colIndex: ci,
        })
        gridKeys.add(colKey)
      }
    }

    const colIndices = Array.from({ length: dimCount }, (_, i) => i)

    const parentLabelRaw = entries.find((e) => e.group.parentLabel?.trim())?.group.parentLabel
    const parentLabelTrimmed = (parentLabelRaw ?? '').trim()
    const trimmed = parentStem.replace(/^[A-Z]_/i, '').replace(/_0$/, '')
    // A usable parentLabel wins; otherwise (empty OR a bare variable code) derive
    // the stem from member cell labels before falling back to humanizing the code.
    const parentLabelUsable =
      parentLabelTrimmed.length > 0 && !isVariableCodeLabel(parentLabelTrimmed)
    const derivedStem = parentLabelUsable
      ? ''
      : deriveGroupStemFromMemberLabels(cells.map((c) => c.fullLabel))
    const stem = parentLabelUsable
      ? parentLabelTrimmed
      : derivedStem || humanizeColumnName(trimmed || parentStem)

    gridFamilies.push({
      prefix: parentStem,
      stem,
      cells,
      rowIndices,
      colIndices,
      colLabels: dimLabels,
      brandNames,
    })
  }

  // Step 3: Build StemGroups from non-grid Phase 0 groups.
  const groups: StemGroup[] = []
  const groupKeys = new Set<string>()

  for (const g of standaloneGroups) {
    const items: StemGroupItem[] = []
    for (const colKey of g.columns) {
      const col = columnIndex.get(colKey)
      if (!col) continue
      const cleaned = cleanLabel(col.label, originalNames?.[colKey])
      const parts = cleaned.split(STEM_SEPARATOR)
      const itemLabel = (parts.length >= 2 && parts[0].trim().length > 15)
        ? parts.slice(1).join(' ').trim()
        : cleaned
      items.push({ itemLabel, fullLabel: col.label, key: colKey })
      groupKeys.add(colKey)
    }
    if (items.length < 2) {
      for (const item of items) groupKeys.delete(item.key)
      continue
    }

    const trimmed = g.groupId
      .replace(/^pfx_/, '')
      .replace(/^mr_\$?/, '')
      .replace(/^slot_/, '')
      .replace(/^qtxt_/, '')
      .replace(/^[A-Z]_/i, '')
      .replace(/_0r?$/, '')
    const parentLabelTrimmed = (g.parentLabel ?? '').trim()
    // A usable parentLabel wins; otherwise (empty OR a bare variable code such as
    // "Q7r1" from a persisted column_groups artifact) derive the question stem
    // from member labels before falling back to humanizing the group id.
    const parentLabelUsable =
      parentLabelTrimmed.length > 0 && !isVariableCodeLabel(parentLabelTrimmed)
    const derivedStem = parentLabelUsable
      ? ''
      : deriveGroupStemFromMemberLabels(items.map((i) => i.fullLabel))
    const stem = parentLabelUsable
      ? parentLabelTrimmed
      : derivedStem || humanizeColumnName(trimmed || g.groupId)
    groups.push({ stem, items })
  }

  // Step 4: Build ordered sequence preserving questionnaire order.
  const allGroupedKeys = new Set([...gridKeys, ...groupKeys])
  const singles: { fullLabel: string; key: string }[] = []
  const ordered: OrderedStemEntry[] = []
  const emittedGrids = new Set<string>()
  const emittedGroups = new Set<string>()

  // Build reverse lookups: column key → which grid/group it belongs to.
  const keyToGrid = new Map<string, GridFamily>()
  for (const fam of gridFamilies) {
    for (const cell of fam.cells) keyToGrid.set(cell.key, fam)
  }
  const keyToGroup = new Map<string, StemGroup>()
  for (const g of groups) {
    for (const item of g.items) keyToGroup.set(item.key, g)
  }

  for (const col of columns) {
    const grid = keyToGrid.get(col.key)
    if (grid) {
      if (!emittedGrids.has(grid.prefix)) {
        emittedGrids.add(grid.prefix)
        ordered.push({ type: 'grid', family: grid })
      }
      continue
    }
    const group = keyToGroup.get(col.key)
    if (group) {
      if (!emittedGroups.has(group.stem)) {
        emittedGroups.add(group.stem)
        ordered.push({ type: 'group', group })
      }
      continue
    }
    singles.push({ fullLabel: col.label, key: col.key })
    ordered.push({ type: 'single', fullLabel: col.label, key: col.key })
  }

  return { groups, singles, ordered, gridFamilies }
}


/**
 * Format a group label for inline display: "Stem text (N items)".
 */
export function formatGroupLabel(stem: string, count: number): string {
  return `${stem} (${count} items)`
}

/**
 * Extract the item portion from a label that belongs to a stem group.
 * Returns the full label if no separator is found.
 */
export function extractItemLabel(label: string): string {
  const parts = label.split(STEM_SEPARATOR)
  if (parts.length >= 2 && parts[0].trim().length > 10) {
    return parts.slice(1).join(' ').trim()
  }
  return label
}

/**
 * Split a column label into its question stem and item portion, applying the
 * same rule {@link groupColumnsBySharedStem} uses internally (stem > 15 chars,
 * item > 3 chars). Unlike that function's inline logic, this ALWAYS runs the
 * label through {@link cleanLabel} first so code prefixes (e.g. "S1r21oe:")
 * are stripped even when no SPSS `originalName` anchor is available — this is
 * what lets lone-stem columns (a single column whose label is
 * "Q…industry? - Other (please specify)") be displayed as question → option.
 *
 * Returns `null` when the label has no meaningful split (plain fields like
 * "Age"), in which case callers should render it flat.
 */
export function splitStemAndItem(
  label: string,
  originalName?: string,
): { stem: string; item: string } | null {
  const cleaned = originalName ? cleanLabel(label, originalName) : cleanLabel(label)
  const parts = cleaned.split(STEM_SEPARATOR)
  if (
    parts.length >= 2 &&
    parts[0].trim().length > 15 &&
    parts.slice(1).join(' ').trim().length > 3
  ) {
    return { stem: parts[0].trim(), item: parts.slice(1).join(' ').trim() }
  }
  return null
}

/**
 * Display text for a segment filter value. Resolution order:
 * 1. `valueMap` (user-defined demographic mapping, e.g. "45-59 years old" → "45-54")
 * 2. `valueLabels` (SPSS code → label from file metadata)
 * 3. Raw value as-is
 *
 * Normalizes numeric keys so `"1.0"` matches SAV maps keyed as `"1"` (see Python
 * `_normalise_value_label_key` in survey_engine).
 */
export function displaySegmentValueLabel(
  col: {
    valueLabels?: Record<string, string> | undefined
    valueMap?: Record<string, string> | undefined
  },
  rawValue: string,
): string {
  if (col.valueMap) {
    const mapped = col.valueMap[rawValue]
    if (mapped !== undefined) return mapped
  }
  const labels = col.valueLabels
  if (!labels) return rawValue
  const direct = labels[rawValue]
  if (direct !== undefined) return direct
  const n = Number(rawValue)
  if (Number.isFinite(n) && Number.isInteger(n)) {
    const mapped = labels[String(n)]
    if (mapped !== undefined) return mapped
  }
  return rawValue
}
